import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { getOfficeArchivedServices, restoreOfficeService, sortObj } from "../../redux/slices/OfficeSlice";
import { Service } from "../../models/service.model";
import { TableHeader, TableRow, Pagination } from "../../components";
import { headersArchivedService } from "./ServiceScreenDaa";
import { formatNumToDec, getSorting } from "../../utils";
import { CircularProgress } from "@mui/material";
import DataNotFound from "../../components/DataNotFound/DataNotFound";
import NotFound from "../../assets/images/no-services-found.png";
import { Button } from "../../components";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { useContext } from "react";
import { ToastVariants } from "../../models";

interface ArchivedServicesProps {
  officeId: string;
}

const ArchivedServices: React.FC<ArchivedServicesProps> = ({ officeId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const { archivedServices, loading } = useSelector((state: RootState) => state.office);
  const [sort, setSort] = useState<sortObj>({
    pageNumber: 0,
    rowsPerPage: 10,
    sortedBy: "name,asc",
  });
  const [sortMethods, setSortMethods] = useState([true, false, false, false]);
  const [activeColumn, setActiveColumn] = useState<number>(-1);
  const [restoringServiceId, setRestoringServiceId] = useState<string | null>(null);

  const headers = headersArchivedService;
  const sortKeys = headers.map(head => head.sortKey);
  const extraClasses = headers.map(head => head.extraClasses);

  const headerIconAsc = <p> ↑</p>;
  const headerIconDesc = <p> ↓</p>;
  const sortIcons = sortMethods.map((item, index) => (activeColumn === index ? (item ? headerIconAsc : headerIconDesc) : null));

  const fetchArchivedServices = async (sortObj?: sortObj) => {
    const pageNumToBeCalled = sortObj?.pageNumber ?? sort.pageNumber;
    const sizeToBeCalled = sortObj?.rowsPerPage ?? sort.rowsPerPage;
    const sortBy = sortObj?.sortedBy ?? sort.sortedBy;

    await dispatch(
      getOfficeArchivedServices({
        officeId,
        page: pageNumToBeCalled,
        size: sizeToBeCalled,
        sort: sortBy,
      })
    );
  };

  useEffect(() => {
    if (officeId) {
      fetchArchivedServices();
    }
  }, [officeId]);

  const onPageChange = async (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    const newSort = { ...sort, pageNumber: newPage };
    setSort(newSort);
    await fetchArchivedServices(newSort);
  };

  const onRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newSort = { ...sort, pageNumber: 0, rowsPerPage: parseInt(event.target.value, 10) };
    setSort(newSort);
    await fetchArchivedServices(newSort);
  };

  const onHeaderClick = (index: number) => {
    if (sortKeys[index] === "" || loading) return;
    const sortMethodString = getSorting(sortKeys, index, sortMethods);
    setActiveColumn(index);
    setSortMethods([...sortMethods.slice(0, index), !sortMethods[index], ...sortMethods.slice(index + 1)]);
    const newSort = { ...sort, sortedBy: sortMethodString, pageNumber: 0 };
    setSort(newSort);
    fetchArchivedServices(newSort);
  };

  const handleRestore = async (serviceId: string) => {
    if (!serviceId || !officeId) return;

    try {
      setRestoringServiceId(serviceId);
      const result = await dispatch(restoreOfficeService({ officeId, serviceId }));
      if (result.payload) {
        toast?.openToast("Service restored successfully", 2000, ToastVariants.Success);
        // Refresh the archived services list
        await fetchArchivedServices(sort);
      } else {
        toast?.openToast("Failed to restore service", 2000, ToastVariants.Error);
      }
    } catch (error) {
      toast?.openToast("Failed to restore service", 2000, ToastVariants.Error);
    } finally {
      setRestoringServiceId(null);
    }
  };

  if (loading && !archivedServices) {
    return <CircularProgress size={40} style={{ marginLeft: "50%", marginTop: "10%" }} />;
  }

  return (
    <div className="block">
      {archivedServices?.content && archivedServices.content.length > 0 ? (
        <>
          <div className="flex justify-between items-center border-b border-docnote-blue-light border-opacity-50">
            {headers.map((head, index: number) => (
              <TableHeader
                title={head.title}
                index={index}
                key={index}
                sortIcon={sortIcons[index]}
                isClickable={head.isClickable}
                handleSort={onHeaderClick}
                extraClassesHeader={extraClasses[index]}
              />
            ))}
          </div>

          {archivedServices.content.map((rowData: Service, index: number) => {
            const sortedData = {
              service: rowData.name,
              description: rowData.description || "",
              price: "$" + formatNumToDec(rowData.price / 100, 2),
              action: (
                <div onClick={e => e.stopPropagation()}>
                  <Button
                    text="Restore"
                    onClick={() => handleRestore(rowData.serviceId || "")}
                    disabled={restoringServiceId === rowData.serviceId || loading}
                    className="whitespace-nowrap w-auto"
                  />
                </div>
              ),
            };

            return (
              <TableRow
                data={Object.values(sortedData)}
                key={index}
                id={rowData.serviceId}
                extraClassesRow={extraClasses}
                tooltipVisible={false}
                handleOpenTooltip={() => {}}
                handleCloseTooltip={() => {}}
              />
            );
          })}

          <Pagination
            colSpan={4}
            count={archivedServices.totalElements || 0}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            page={sort.pageNumber}
            rowsPerPage={sort.rowsPerPage}
          />
        </>
      ) : (
        <DataNotFound image={NotFound} text="No archived services found" />
      )}
    </div>
  );
};

export default ArchivedServices;
